import { type FC, useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetWorkflow, useListWorkflowExecutions, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName, workflowsUrl, hasManualTrigger, isWorkflowDeleting } from "@/utils/automations";
import { WorkflowExecutionsList } from "@/components/automations/AutomationExecutionsList";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { useMembers } from "@/queries/organization-queries";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationPropertiesHeader } from "../AutomationPropertiesHeader";
import { AutomationDefinitionSection } from "./AutomationDefinitionSection";
import AutomationEditDialog from "@/components/automations/AutomationEditDialog";
import { editableStepsAtom, isEditingStepsAtom } from "./state";
import { convertWorkflowActionToFormSteps } from "@/components/automations/automation-edit-form-data";
import { useSetAtom, useAtomValue } from "jotai";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import IconPencil from "central-icons/IconPencil";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";
import { SaveAutomationStepsButton } from "@/routes/automations/automation-details-page/SaveAutomationStepsButton";
import { Banner } from "@/components/flexkit/Banner";

const IconPencilWithSize = withGitpodSize(IconPencil);

enum WorkflowDetailsTab {
    Definition = "definition",
    Executions = "executions",
}

export const AutomationDetailsPage: FC = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const hasShownErrorToast = useRef(false);
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const {
        data: workflowExecutions,
        error: workflowExecutionsError,
        refetch: refetchExecutions,
    } = useListWorkflowExecutions({
        enabled: !!workflowId,
        workflowId: workflowId || "",
    });
    const { data: members } = useMembers();
    const [selectedTab, setSelectedTab] = useState(WorkflowDetailsTab.Executions);
    const [workflowBeingEdited, setWorkflowBeingEdited] = useState<PlainWorkflow | null>(null);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const location = useLocation();

    // when leaving the page, turn off edit mode
    // so that when revisting the page, the edit mode is off
    useEffect(
        () => () => {
            setIsEditingSteps(false);
        },
        [setIsEditingSteps],
    );

    useEffect(() => {
        if (workflowExecutionsError && !hasShownErrorToast.current) {
            hasShownErrorToast.current = true;
            toast({
                title: "Failed to load executions",
                description: formatError(workflowExecutionsError),
            });
        }
        // Reset the flag when error is cleared
        if (!workflowExecutionsError && hasShownErrorToast.current) {
            hasShownErrorToast.current = false;
        }
    }, [workflowExecutionsError, toast]);

    useEffect(() => {
        switch (location.hash) {
            case `#${WorkflowDetailsTab.Definition}`:
                setSelectedTab(WorkflowDetailsTab.Definition);
                if (location.state?.edit) {
                    setIsEditingSteps(true);
                }
                break;
            case `#${WorkflowDetailsTab.Executions}`:
                setSelectedTab(WorkflowDetailsTab.Executions);
                break;
            default:
                // if no hash is set, navigate to the executions tab
                navigate(`#${WorkflowDetailsTab.Executions}`, { replace: true });
        }
    }, [location.hash, location.state?.edit, navigate, setIsEditingSteps]);

    useEffect(() => {
        // convert the workflow action to editable steps
        // the step list uses this to render the steps
        if (workflow?.spec?.action) {
            setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec.action));
        } else {
            setEditableSteps(null);
        }
    }, [workflow, setEditableSteps]);

    const handleEdit = () => {
        if (workflow) {
            setWorkflowBeingEdited(workflow);
        }
    };

    const changeTab = (value: string) => {
        navigate(`#${value}`, { replace: true });
    };

    const isDeleting = isWorkflowDeleting(workflow);

    return (
        <div data-testid="workflow-page" className="flex min-h-full flex-col gap-4">
            {workflow ? (
                <div className="flex flex-col gap-2">
                    {/* Header row: breadcrumb + action buttons */}
                    <div className="flex items-center justify-between">
                        <Breadcrumbs
                            customBreadcrumbs={[
                                { label: "Automations", to: workflowsUrl() },
                                { label: workflowName(workflow) },
                            ]}
                        />
                        <div className="flex items-center gap-2">
                            {/* We don't show the run action in the dropdown as we have a dedicated run button next to it */}
                            <WorkflowActionsDropdown workflow={workflow} showRunAction={false} buttonSize="md" />
                            <AutomationDetailsPageActionButtons workflow={workflow} />
                        </div>
                    </div>
                    {/* Description and trigger info */}
                    <div className="flex flex-col gap-1">
                        <Text className="text-base text-content-strong">
                            {workflow.metadata?.description || "No description provided."}
                        </Text>
                        <AutomationPropertiesHeader workflow={workflow} onEdit={handleEdit} />
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            {isDeleting && (
                <Banner
                    variant="info"
                    className="mt-4"
                    text="This automation is being deleted. All interactions are disabled."
                />
            )}

            <ErrorMessage error={workflowError} />

            <Tabs className="flex grow flex-col" value={selectedTab} onValueChange={changeTab}>
                {!isEditingSteps && (
                    <TabsList className="mb-6 w-fit">
                        <TabsTrigger value={WorkflowDetailsTab.Executions}>Executions</TabsTrigger>
                        <TabsTrigger value={WorkflowDetailsTab.Definition}>Definition</TabsTrigger>
                    </TabsList>
                )}

                <TabsContent value={WorkflowDetailsTab.Definition} className="flex flex-col data-[state=active]:grow">
                    <AutomationDefinitionSection />
                </TabsContent>

                <TabsContent value={WorkflowDetailsTab.Executions} className="flex flex-col data-[state=active]:grow">
                    <WorkflowExecutionsList
                        executions={workflowExecutions || []}
                        members={members?.members}
                        workflow={workflow}
                        hasManualTrigger={hasManualTrigger(workflow)}
                        error={workflowExecutionsError}
                        onRetry={() => refetchExecutions()}
                    />
                </TabsContent>
            </Tabs>
            <AutomationEditDialog
                open={!!workflowBeingEdited}
                onOpenChange={(open) => {
                    if (!open) {
                        setWorkflowBeingEdited(null);
                    }
                }}
                workflow={workflowBeingEdited ?? undefined}
            />
        </div>
    );
};

const AutomationDetailsPageActionButtons: FC<{ workflow: PlainWorkflow }> = ({ workflow }) => {
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const setEditableSteps = useSetAtom(editableStepsAtom);
    const location = useLocation();
    const navigate = useNavigate();

    const handleEdit = useCallback(() => {
        // if the user is on the executions tab and they click edit
        // take them to the definitions tab
        if (location.hash !== `#${WorkflowDetailsTab.Definition}`) {
            navigate(`#${WorkflowDetailsTab.Definition}`, { replace: true });
        }
        setIsEditingSteps(true);
    }, [setIsEditingSteps, navigate, location.hash]);

    const handleCancel = useCallback(() => {
        setIsEditingSteps(false);
        setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec?.action));
    }, [setIsEditingSteps, setEditableSteps, workflow.spec?.action]);

    if (isEditingSteps) {
        return (
            <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                    Cancel
                </Button>
                <SaveAutomationStepsButton workflowId={workflow.id} />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Button variant="outline" LeadingIcon={IconPencilWithSize} onClick={handleEdit} data-tracking-id="edit">
                Edit
            </Button>
            {hasManualTrigger(workflow) && <RunAutomationButton workflowId={workflow.id} data-tracking-id="run" />}
        </div>
    );
};
